export function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0)
}

/** Valor numérico → string para input (sem símbolo R$). */
export function formatCurrencyInput(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) return ''
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value)
}

/**
 * Parse de moeda BR: aceita "4,95", "4.95", "R$ 4,95" e "1.234,56".
 *
 * Sem vírgula, o ponto é decimal ("4.95" = 4,95), salvo quando forma grupos de
 * três dígitos ("1.234" = mil duzentos e trinta e quatro), que é como o
 * separador de milhar é escrito no Brasil.
 *
 * Para quantidade digitada em campo numérico use parseQuantidadeBr: lá o ponto
 * é sempre decimal, porque é assim que o input type=number entrega o valor.
 */
export function parseCurrencyBr(value: string): number {
  const trimmed = value.trim()
  if (!trimmed) return 0
  const cleaned = trimmed.replace(/[^\d,.-]/g, '')
  if (!cleaned) return 0

  let normalized: string
  if (cleaned.includes(',')) {
    normalized = cleaned.replace(/\./g, '').replace(',', '.')
  } else if (/^-?\d{1,3}(\.\d{3})+$/.test(cleaned)) {
    normalized = cleaned.replace(/\./g, '')
  } else {
    normalized = cleaned
  }

  const n = Number(normalized)
  return Number.isFinite(n) ? n : 0
}

/**
 * Quantidade vinda de input type=number: o ponto é sempre decimal, nunca
 * separador de milhar. Devolve NaN em entrada inválida, para o chamador recusar.
 */
export function parseQuantidadeBr(value: string): number {
  const cleaned = value.trim().replace(/[^\d,.-]/g, '').replace(',', '.')
  if (!cleaned || cleaned === '-') return Number.NaN
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : Number.NaN
}

export function formatNumber(value: number, digits = 2) {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(value || 0)
}

export function formatDate(iso: string) {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

/** Data de hoje (calendário local) em YYYY-MM-DD. */
export function todayISO() {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Primeiro e último dia do mês (calendário local), em YYYY-MM-DD. */
export function monthBoundsISO(d = new Date()) {
  const y = d.getFullYear()
  const m = d.getMonth()
  const pad = (n: number) => String(n).padStart(2, '0')
  const lastDay = new Date(y, m + 1, 0).getDate()
  return {
    inicio: `${y}-${pad(m + 1)}-01`,
    fim: `${y}-${pad(m + 1)}-${pad(lastDay)}`,
  }
}

export function downloadText(filename: string, content: string, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function toCsv(rows: Record<string, unknown>[], headers?: string[]) {
  if (!rows.length) return ''
  const cols = headers ?? Object.keys(rows[0])
  const escape = (v: unknown) => {
    const s = v == null ? '' : String(v)
    return `"${s.replace(/"/g, '""')}"`
  }
  const lines = [cols.join(',')]
  for (const row of rows) {
    lines.push(cols.map((c) => escape(row[c])).join(','))
  }
  return lines.join('\n')
}

const CSV_DELIMITER_ERROR =
  'Delimitador CSV não suportado. Use vírgula (,) ou ponto e vírgula (;). Separadores como tabulação ou pipe (|) não são aceitos.'

/**
 * Parse CSV com detecção de `,` (padrão) ou `;` (Excel pt-BR).
 * Rejeita delimitadores inválidos (tab, pipe) com erro legível.
 * Coluna única (sem delimitador) continua válida.
 */
export function parseCsv(text: string): Record<string, string>[] {
  const lines = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .filter((l) => l.trim())
  if (lines.length < 2) return []
  const delimiter = detectCsvDelimiter(lines[0])
  const headers = splitCsvLine(lines[0], delimiter).map((h) => h.trim())
  return lines.slice(1).map((line) => {
    const cells = splitCsvLine(line, delimiter)
    const row: Record<string, string> = {}
    headers.forEach((h, i) => {
      row[h] = (cells[i] ?? '').trim()
    })
    return row
  })
}

/** Conta ocorrências de um caractere fora de aspas CSV. */
function countUnquoted(line: string, target: string): number {
  let count = 0
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        i++
      } else if (ch === '"') {
        inQuotes = false
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === target) {
      count++
    }
  }
  return count
}

/**
 * Detecta `,` ou `;` pelo cabeçalho. Empate → vírgula.
 * Sem `,`/`;`: aceita coluna única; rejeita tab/pipe.
 */
export function detectCsvDelimiter(headerLine: string): ',' | ';' {
  const commas = countUnquoted(headerLine, ',')
  const semis = countUnquoted(headerLine, ';')
  if (semis > commas) return ';'
  if (commas > 0) return ','
  if (countUnquoted(headerLine, '\t') > 0 || countUnquoted(headerLine, '|') > 0) {
    throw new Error(CSV_DELIMITER_ERROR)
  }
  // Coluna única (sem delimitador): vírgula é irrelevante no split.
  return ','
}

function splitCsvLine(line: string, delimiter: ',' | ';' = ','): string[] {
  const result: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"'
        i++
      } else if (ch === '"') {
        inQuotes = false
      } else {
        cur += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === delimiter) {
      result.push(cur)
      cur = ''
    } else {
      cur += ch
    }
  }
  result.push(cur)
  return result
}

export function statusContratoClass(status: string) {
  switch (status) {
    case 'ok':
      return 'badge badge-ok'
    case 'atencao':
      return 'badge badge-atencao'
    case 'critico':
      return 'badge badge-critico'
    case 'excedido':
      return 'badge badge-excedido'
    default:
      return 'badge badge-zerado'
  }
}

export function statusEstoque(dias: number | null): { label: string; className: string } {
  if (dias == null || Number.isNaN(dias)) {
    return { label: '—', className: 'badge badge-neutral' }
  }
  if (dias < 7) return { label: 'Crítico', className: 'badge badge-critico' }
  if (dias <= 15) return { label: 'Atenção', className: 'badge badge-atencao' }
  return { label: 'OK', className: 'badge badge-ok' }
}
